import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Color, PHONE_TEXTS_ROUTE, PHONE_THREAD_ROUTE, Size, fill, formatPhone, pageKindLineText, t, textConversationPath, textedNumberLeadValues,
  type ConversationThread, type TextThreadRow,
} from '@bms2/shared';
import { Button, Textarea } from '../../components/controls';
import { LoadFailure } from '../../components/composites';
import { useNotice } from '../../components/controls/useNotice';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { PageModule } from '../../shell/PageModule';
import { post, useApi } from '../records/api';
import { NewRecordModal } from '../records/NewRecordModal';
import { TextThreadList } from '../records/TextThreadList';
import styles from './TextThreadPage.module.css';

export { PHONE_THREAD_ROUTE };

/**
 * 🔒 One conversation on one of the organization's numbers (decision 11860): the texts as the Person page's Texts tile
 * draws them, then the reply box, which sends from the conversation's own number and nowhere else (decision 11782). A
 * number no Person holds is read, its reply is refused, and Create Lead opens the texted-card offer's form.
 */
export function TextThreadPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const theirs = useParams().number ?? '';
  const [search] = useSearchParams();
  const ours = search.get('on') ?? '';
  const path = textConversationPath(theirs, ours);
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const fetched = useApi<ConversationThread>(path, apiBaseUrl);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [leadOpen, setLeadOpen] = useState(false);
  const [noticeBanner, setNotice] = useNotice();
  const thread = fetched.state.kind === 'ready' ? fetched.state.data : null;
  const title = thread?.personName ?? (formatPhone(theirs) || theirs);
  const ourText = ours === '' ? t('phonePages.texts.companyNumber') : formatPhone(ours);
  const onLine = [t('phonePages.texts.on'), ourText].join(' ');

  const send = async () => {
    setBusy(true);
    setRefusal(null);
    const newest = thread?.rows[thread.rows.length - 1]?.id ?? null;
    const sent = await post<TextThreadRow>(path, { body: draft, after: newest }, apiBaseUrl);
    setBusy(false);
    if (!sent.ok) {
      setRefusal(`${t('textThread.notSent')} ${sent.message}`);
      return;
    }
    setDraft('');
    fetched.reload();
  };

  return (
    <PageModule title={title} subtitle={pageKindLineText({ page: 'named', route: PHONE_THREAD_ROUTE })} icon="phoneTexts">
      <div className={styles.page} data-text-thread={theirs} data-text-thread-on={ours}>
        <p className={styles.line}>
          <Link to={PHONE_TEXTS_ROUTE}>{t('phonePages.texts.back')}</Link>
          <span>{onLine}</span>
        </p>
        {noticeBanner}
        {fetched.state.kind === 'loading' ? <p className={styles.quiet}>{t('async.loading')}</p> : null}
        {fetched.state.kind === 'failed' ? <LoadFailure state={fetched.state} onRetry={fetched.reload} /> : null}
        {thread ? (
          <>
            {thread.rows.length === 0 ? <p className={styles.quiet}>{t('phonePages.texts.empty')}</p> : <TextThreadList rows={thread.rows} zone={zone} dateFormat={dateFormat} />}
            {thread.personId === null ? (
              <div className={styles.unknown} data-text-thread-unknown="true">
                <p className={styles.quiet}>{t('phonePages.texts.unknown')}</p>
                <Button size={Size.Small} color={Color.Navy} text={[t('phonePages.texts.createLead')]} actions={[() => setLeadOpen(true)]} />
              </div>
            ) : null}
            {thread.replies ? (
              <div className={styles.compose}>
                <Textarea
                  size={Size.Large}
                  label={fill(t('textThread.boxTo'), { number: formatPhone(thread.theirNumber) })}
                  labelHidden
                  value={draft}
                  onChange={setDraft}
                  placeholder={t('textThread.placeholder')}
                  rows={2}
                  disabled={busy}
                  name={`phone-text-${theirs}`}
                />
                <Button size={Size.Small} text={[busy ? t('textThread.sending') : t('textThread.send')]} actions={[() => void send()]} disabled={busy || draft.trim() === ''} />
              </div>
            ) : thread.personId !== null ? <p className={styles.quiet}>{t('phonePages.texts.noReply')}</p> : null}
            {refusal ? <p className={styles.failed} role="alert">{refusal}</p> : null}
          </>
        ) : null}
      </div>
      {/* 🔨 Mounted only while open, so each opening reads the number afresh (CLAUDE.md, the hidden modal's initial prop). */}
      {leadOpen ? (
        <NewRecordModal
          key={`lead-${theirs}`}
          open
          recordType="Lead"
          apiBaseUrl={apiBaseUrl}
          openedFrom="new"
          initialValues={textedNumberLeadValues(theirs)}
          onCancel={() => setLeadOpen(false)}
          onCreated={() => {
            setLeadOpen(false);
            setNotice({ tone: 'good', text: t('phonePages.texts.leadMade') });
          }}
        />
      ) : null}
    </PageModule>
  );
}
